import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  PopupWindow,
  Table,
  MessagePopupCore,
  objectAtPath,
} from '@utahdts/dts-react-common';
import Icon from '../misc/Icon';
import columns from './columns/CompanyColumns';
import webservice from '../app/webservice/webservice';
import Company from './modals/Company';
import roles from '../app/roles/roles'; // Assuming roles.orgAdmin is defined here
import security from '../app/roles/security';
import { sortByLastUpdatedDesc } from '../misc/Shared';

const Companies = () => {
  const [isBusy, setBusy] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [showCompany, displayCompany] = useState(false);
  const [showDelete, displayDelete] = useState(false);
  const [showApproval, displayApproval] = useState(false);
  const [company, setCompany] = useState({}); // This is the company being edited/viewed in the modal
  const [isNew, setIsNew] = useState(true);
  // const [error, setError] = useState(undefined); // setError seems unused, consider removing

  const displayError = () => {
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message: 'An error occurred. Please try again later.',
    });
  };

  const editCompany = (isNewCompany, companyData, showModal) => {
    setIsNew(isNewCompany);
    setCompany(companyData);
    displayCompany(showModal);
  };

  const inactivateCompany = (companyData) => {
    setCompany(companyData);
    displayDelete(true);
  };

  const approveCompany = (companyData) => {
    setCompany(companyData);
    displayApproval(true);
  };

  const resetCompany = (refresh) => {
    editCompany(true, {}, false); // Reset to a new, empty company form
    // setError(undefined); // If setError is kept, reset it here
    displayDelete(false);
    displayApproval(false);
    if (refresh) {
      // setBusy(true); // getCompaniesRefined will set busy state
      getCompaniesRefined();
    }
  };

  const saveContact = (user) => {
    console.log(`[Companies.jsx] saveContact CALLED. User ID: ${user?.id}`);
    if (!user || !user.id) {
      console.error(
        `[Companies.jsx] saveContact - User or user.id is undefined. Cannot save.`,
      );
      return Promise.reject(
        new Error('User or user.id is undefined in saveContact.'),
      );
    }
    return webservice.user.save(user);
  };

  const setContact = (user, field, currentCompanyId) => {
    console.log(
      `[Companies.jsx] setContact CALLED. Field: ${field}, User ID: ${user?.id}, Company ID: ${currentCompanyId}`,
    );
    if (!user) {
      console.error(
        `[Companies.jsx] setContact - User object is null or undefined for field ${field}.`,
      );
      return Promise.reject(
        new Error(
          `User object is null or undefined for field ${field} in setContact.`,
        ),
      );
    }
    if (!currentCompanyId) {
      console.error(
        `[Companies.jsx] setContact - currentCompanyId was not provided.`,
      );
      return Promise.reject(
        new Error('currentCompanyId was not provided to setContact.'),
      );
    }

    const rolesToProcess = Array.isArray(user.roles) ? [...user.roles] : [];
    let isOrgAdminIndex = rolesToProcess.findIndex(
      (role) => role.org === currentCompanyId && role.role === roles.orgAdmin,
    );
    let isFieldIndex = rolesToProcess.findIndex(
      (role) => role.org === currentCompanyId && role[field],
    );

    if (isOrgAdminIndex >= 0) {
      rolesToProcess[isOrgAdminIndex] = {
        ...rolesToProcess[isOrgAdminIndex],
        [field]: true,
      };
      if (isFieldIndex >= 0 && isOrgAdminIndex !== isFieldIndex) {
        rolesToProcess.splice(isFieldIndex, 1);
      }
    } else if (isFieldIndex >= 0) {
      rolesToProcess[isFieldIndex] = {
        ...rolesToProcess[isFieldIndex],
        role: roles.orgAdmin,
        [field]: true,
      };
    } else {
      rolesToProcess.push({
        org: currentCompanyId,
        role: roles.orgAdmin,
        [field]: true,
      });
    }
    const updatedUser = { ...user, roles: rolesToProcess };
    console.log(
      `[Companies.jsx] setContact - User for field ${field} before saveContact:`,
      JSON.stringify(updatedUser),
    );
    return saveContact(updatedUser);
  };

  const checkContact = (companyData, oldContact, field) => {
    console.log(
      `[Companies.jsx] checkContact CALLED. Field: ${field}, Company ID: ${companyData?.id}, OldContact ID: ${oldContact?.id}`,
    );
    if (!companyData || !companyData[field]) {
      console.warn(
        `[Companies.jsx] checkContact - Company or company.${field} is undefined for field: ${field}.`,
      );
      return Promise.resolve(
        `No action needed for ${field}: company or field data missing.`,
      );
    }

    if (companyData[field]?.id !== oldContact?.id) {
      console.log(
        `[Companies.jsx] checkContact - IDs differ for ${field}. Old: ${oldContact?.id}, New: ${companyData[field]?.id}`,
      );
      const operations = [];

      if (oldContact?.id && Array.isArray(oldContact.roles)) {
        const fieldRoleIndex = oldContact.roles.findIndex(
          (role) => role.org === companyData.id && role[field],
        );
        if (fieldRoleIndex >= 0) {
          console.log(
            `[Companies.jsx] checkContact - Found existing role for old contact (${oldContact.id}) for ${field}. Modifying.`,
          );
          const updatedOldContactRoles = oldContact.roles.map((role, index) =>
            index === fieldRoleIndex ? { ...role, [field]: false } : role,
          );
          const updatedOldContact = {
            ...oldContact,
            roles: updatedOldContactRoles,
          };
          operations.push(saveContact(updatedOldContact));
        }
      }
      operations.push(setContact(companyData[field], field, companyData.id));

      if (operations.length > 0) {
        return Promise.all(operations)
          .then(() => `Successfully processed contact changes for ${field}.`)
          .catch((err) => {
            console.error(
              `[Companies.jsx] checkContact - Error during operations for ${field}:`,
              err,
            );
            throw err; // Re-throw to be caught by the main Promise.all in saveCompany
          });
      } else {
        return Promise.resolve(
          `No specific DB operations for ${field} contact changes, though IDs differed.`,
        );
      }
    } else {
      console.log(
        `[Companies.jsx] checkContact - IDs are the same for ${field} (${companyData[field]?.id}). Ensuring contact roles.`,
      );
      return setContact(companyData[field], field, companyData.id);
    }
  };

  const saveCompany = (companyToSave, oldPrimary, oldAlt) => {
    console.log(
      '[Companies.jsx] saveCompany CALLED. Company ID:',
      companyToSave?.id,
    );
    setBusy(true);

    try {
      // Outer try-catch for synchronous errors
      const promises = [];
      if (companyToSave.primaryContact?.id) {
        const primaryPromise = checkContact(
          companyToSave,
          oldPrimary,
          'primaryContact',
        );
        if (!(primaryPromise instanceof Promise)) {
          console.error(
            '[Companies.jsx] CRITICAL: checkContact for primary did not return a Promise!',
          );
          throw new Error('checkContact (primary) failed to return a promise.'); // Make it fail fast
        }
        promises.push(primaryPromise);
      }
      if (companyToSave.alternativeContact?.id) {
        const altPromise = checkContact(
          companyToSave,
          oldAlt,
          'alternativeContact',
        );
        if (!(altPromise instanceof Promise)) {
          console.error(
            '[Companies.jsx] CRITICAL: checkContact for alternative did not return a Promise!',
          );
          throw new Error(
            'checkContact (alternative) failed to return a promise.',
          ); // Make it fail fast
        }
        promises.push(altPromise);
      }

      const companySavePromise = webservice.company.save(companyToSave);
      if (!(companySavePromise instanceof Promise)) {
        console.error(
          '[Companies.jsx] CRITICAL: webservice.company.save did not return a Promise!',
        );
        throw new Error('webservice.company.save failed to return a promise.'); // Make it fail fast
      }
      promises.push(companySavePromise);

      console.log(
        '[Companies.jsx] saveCompany - About to return Promise.all. Number of promises:',
        promises.length,
      );
      return Promise.all(promises)
        .then((results) => {
          console.log('[Companies.jsx] saveCompany - Promise.all RESOLVED.');
          const companySaveOperationResult = results[results.length - 1];
          let companySavePayloadErrors = objectAtPath(
            companySaveOperationResult,
            'errors',
          );
          if (
            !companySavePayloadErrors &&
            companySaveOperationResult &&
            companySaveOperationResult.data
          ) {
            companySavePayloadErrors = objectAtPath(
              companySaveOperationResult.data,
              'errors',
            );
          }

          if (companySavePayloadErrors && companySavePayloadErrors.length > 0) {
            const firstError = companySavePayloadErrors[0];
            const errorMessage =
              firstError?.message || 'Unknown error from save operation';
            if (
              errorMessage.includes('Organization with FEIN') &&
              errorMessage.includes('already exists')
            ) {
              console.log(
                '[Companies.jsx] saveCompany - Duplicate FEIN in resolved payload. graphQLWebservice.js/webservice.js handled dialog.',
              );
              setBusy(false);
              return Promise.reject(new Error(errorMessage)); // Return rejected promise so modal catches it
            } else if (
              errorMessage.includes('Webservice Communication Interruption')
            ) {
              console.log(
                '[Companies.jsx] saveCompany - "Webservice Communication Interruption" in resolved payload. graphQLWebservice.js handled dialog.',
              );
              setBusy(false);
              return Promise.reject(new Error(errorMessage)); // Return rejected promise so modal catches it
            } else {
              console.log(
                '[Companies.jsx] saveCompany - Other error in resolved payload. Calling displayError().',
              );
              displayError();
              return Promise.reject(new Error(errorMessage)); // Return rejected promise so modal catches it
            }
          }
          console.log(
            '[Companies.jsx] saveCompany - All operations successful. Calling resetCompany(true).',
          );
          resetCompany(true);
          return Promise.resolve(); // Explicitly return resolved promise on success
        })
        .catch((err) => {
          console.error(
            '[Companies.jsx] CATCH BLOCK - Promise.all rejected. Raw error:',
            err,
          );
          let errorMessageText = '';
          if (err && err.message && typeof err.message === 'string') {
            errorMessageText = err.message;
          } else if (typeof err === 'string') {
            errorMessageText = err;
          } else if (
            err &&
            Array.isArray(err.graphQLErrors) &&
            err.graphQLErrors.length > 0 &&
            err.graphQLErrors[0]?.message
          ) {
            errorMessageText = err.graphQLErrors[0].message;
          } else if (
            Array.isArray(err) &&
            err.length > 0 &&
            err[0]?.message &&
            typeof err[0].message === 'string'
          ) {
            errorMessageText = err[0].message;
          } else if (err && typeof err === 'object') {
            try {
              errorMessageText = JSON.stringify(err);
            } catch (e) {
              errorMessageText = 'Complex error object';
            }
          }

          console.log(
            '[Companies.jsx] CATCH BLOCK - Extracted errorMessage:',
            JSON.stringify(errorMessageText),
          );

          if (
            errorMessageText.includes('Organization with FEIN') &&
            errorMessageText.includes('already exists')
          ) {
            console.log(
              '[Companies.jsx] CATCH BLOCK - Duplicate FEIN error. graphQLWebservice.js/webservice.js handled dialog.',
            );
            setBusy(false);
          } else if (
            errorMessageText.includes('Internal server error') ||
            errorMessageText.includes('Signed Out')
          ) {
            console.log(
              '[Companies.jsx] CATCH BLOCK - Signed Out error. graphQLWebservice.js handled dialog.',
            );
            setBusy(false);
          } else if (
            errorMessageText.includes('Webservice Communication Interruption')
          ) {
            console.log(
              '[Companies.jsx] CATCH BLOCK - Webservice Communication Interruption. graphQLWebservice.js handled dialog.',
            );
            setBusy(false);
          } else {
            console.log(
              '[Companies.jsx] CATCH BLOCK - Unhandled rejection type. Calling displayError(). Actual errorMessage:',
              JSON.stringify(errorMessageText),
            );
            displayError();
          }
          // Always throw the error so the modal's .catch() can handle it
          throw err;
        });
    } catch (syncError) {
      console.error(
        '[Companies.jsx] saveCompany - SYNCHRONOUS ERROR caught:',
        syncError,
        syncError.stack,
      );
      setBusy(false);
      displayError();
      return Promise.reject(syncError); // Propagate as a rejected promise
    }
  };

  const actualInactivate = () => {
    setBusy(true); // Indicate busy state before async call
    webservice.company
      .inactivate(company.id)
      .then(() => resetCompany(true))
      .catch(displayError) // displayError already calls setBusy(false)
      .finally(() => {
        // Ensure busy is false if not handled by displayError or resetCompany
        if (isBusy) setBusy(false); // Check current state to avoid unnecessary sets
      });
  };

  const actualApproval = () => {
    setBusy(true); // Indicate busy state before async call
    webservice.company
      .approve(company.id)
      .then(() => resetCompany(true))
      .catch(displayError) // displayError already calls setBusy(false)
      .finally(() => {
        if (isBusy) setBusy(false);
      });
  };

  const getCompanies = () => {
    setBusy(true); // Set busy before fetching
    webservice.company
      .all()
      .then((data) => {
        if (data && data.orgs) {
          // Add a check for data.orgs
          setCompanies(data.orgs);
        } else {
          setCompanies([]); // Set to empty array if data.orgs is not available
          console.warn(
            '[Companies.jsx] getCompanies - data.orgs is undefined or null',
            data,
          );
        }
      })
      .catch((err) => {
        // Catch errors from webservice.company.all()
        console.error(
          '[Companies.jsx] getCompanies - Error fetching companies:',
          err,
        );
        setCompanies([]); // Optionally clear companies on error
        displayError(); // displayError calls setBusy(false)
      })
      .finally(() => {
        // setBusy(false) is called by displayError or should be called after setCompanies
        // If no error, setBusy(false) should be called after data processing
        // This is tricky because displayError also sets it.
        // The most reliable way is to ensure setBusy(false) is called in .then after setCompanies
        // and in .catch (which displayError does).
        // For simplicity, if displayError is not called, we ensure it here.
        // However, getCompanies is called by resetCompany(true) which sets busy,
        // so this finally might be redundant if displayError always fires or success always sets it.
        // Let's ensure it's set false after successful data load.
        // The current structure: setBusy(true) -> async -> .then(setCompanies, setBusy(false)) OR .catch(displayError [which sets setBusy(false)])
        // and in .finally (which displayError does).
        // For clarity and to avoid redundancy, the finally block here is removed.
      });
  };

  // Refined getCompanies
  const getCompaniesRefined = () => {
    setBusy(true);
    webservice.company
      .all()
      .then((data) => {
        if (data && data.orgs) {
          setCompanies(data.orgs);
        } else {
          setCompanies([]);
          console.warn(
            '[Companies.jsx] getCompanies - data.orgs is undefined or null',
            data,
          );
        }
        setBusy(false); // Set busy to false after successful data load and state update
      })
      .catch((err) => {
        console.error(
          '[Companies.jsx] getCompanies - Error fetching companies:',
          err,
        );
        setCompanies([]);
        displayError(); // displayError calls setBusy(false)
      });
  };

  // const displayUserError = () => { ... }; // This seems unused in this component's main flows.

  useEffect(() => {
    getCompaniesRefined(); // Use the refined version
  }, []);

  return (
    // ...existing code...
    <div key="listings" className="home-wrapper">
      <div className="home-content">
        <h1 className="mb-zero">Companies</h1>
        {webservice.user && webservice.user.loggedInUser && webservice.user.loggedInUser.orgAdminOrgIds && (
          <p className="mt-spacing small-text" data-testid="org-admin-list">
            Your Organizations: {webservice.user.loggedInUser.orgAdminOrgIds.join(', ')}
          </p>
        )}
      </div>
      <div className="buttons flex">
        <Button
          label="Add Company"
          className="mr-spacing flex align-items-center"
          onClick={() => editCompany(true, {}, true)}
          children={
            <span className="icon--round ml-spacing">{Icon.iconPlus()}</span>
          }
          childrenPostion={Button.CHILDREN_POSITION.AFTER}
        />
      </div>
      <div>
        <Table
          list={sortByLastUpdatedDesc(companies)}
          columns={columns(editCompany, inactivateCompany, approveCompany)}
          filters={true}
          showLimit={50}
          ajaxSpinner={isBusy}
        />
      </div>
      {showCompany && (
        <Company
          onClose={() => resetCompany(false)} // Pass false to avoid re-fetch if just closing modal
          onSave={saveCompany}
          isNew={isNew}
          company={company} // This is the company state variable
          isAdmin={security.hasAnyRole([roles.admin, roles.dev])}
        />
      )}
      {showDelete && (
        <PopupWindow
          className="popup-window--large"
          onCloseButtonCallback={() => resetCompany(false)}
          footerChildren={[
            <Button
              label="Cancel"
              onClick={() => resetCompany(false)}
              key="close-button"
              className="button--black"
            />,
            <Button
              label="Inactivate"
              busy={isBusy}
              onClick={actualInactivate}
              key="save-button"
            />,
          ]}
        >
          <h3>Are you sure?</h3>
          <p>You are about to inactivate a company.</p>
        </PopupWindow>
      )}
      {showApproval && (
        <PopupWindow
          className="popup-window--large"
          onCloseButtonCallback={() => resetCompany(false)}
          footerChildren={[
            <Button
              label="Cancel"
              onClick={() => resetCompany(false)}
              key="close-button"
              className="button--black"
            />,
            <Button
              label="Approve"
              busy={isBusy}
              onClick={actualApproval}
              key="save-button"
            />,
          ]}
        >
          <h3>Are you sure?</h3>
          <p>
            You are about to approve a company. Please review the company info
            before submitting.
          </p>
        </PopupWindow>
      )}
    </div>
  );
};

export default observer(Companies);
